import React from 'react';
import { Link } from 'react-router-dom';
import { LuFlame, LuTrendingUp } from 'react-icons/lu';
import PlanAwareLink from '../billing/PlanAwareLink';
import PlanTierLabel from '../billing/PlanTierLabel';
import { FEATURE } from '../../config/planFeatures';
import { useUserPlan } from '../../hooks/useUserPlan';
import './LearningProgressPanel.css';

export default function LearningProgressPanel({ stats, summary, apiError, loading, className = '' }) {
  const { canUseFeature } = useUserPlan();
  const leaderboardLocked = !canUseFeature(FEATURE.LEADERBOARD_RANK);
  const level = summary?.level ?? stats?.gamificationLevel ?? 1;
  const xp = summary?.lifetimeXp ?? stats?.lifetimeXp ?? 0;
  const score = summary?.learningScore ?? stats?.learningScore ?? 0;
  const streak = summary?.currentStreak ?? stats?.streak ?? 0;
  const xpToNext = summary?.xpToNextLevel ?? stats?.xpToNextLevel ?? 0;
  const levelPct = summary?.levelProgressPercent ?? stats?.levelProgressPercent ?? 0;
  const daily = summary?.dailyGoal;
  const rank = summary?.rank?.globalWeekly;

  const isEmpty = !loading && !apiError && xp === 0 && score === 0;

  return (
    <section className={`lg-progress ${className}`.trim()} aria-label="Learning progress">
      <div className="lg-progress-head">
        <div>
          <p className="lg-progress-eyebrow">
            <LuTrendingUp aria-hidden />
            Learning score
          </p>
          <p className="lg-progress-score">
            <span className="lg-progress-score-value">{score}</span>
            <span className="lg-progress-score-max">/ 100</span>
          </p>
        </div>
        <div className="lg-progress-streak" title="Day streak">
          <LuFlame className="lg-progress-streak-icon" aria-hidden />
          <span>{streak}</span>
          <span className="lg-progress-streak-label">day streak</span>
        </div>
      </div>

      <div className="lg-progress-level">
        <div className="lg-progress-level-row">
          <span>Level {level}</span>
          <span className="lg-progress-xp">{xp.toLocaleString()} XP</span>
        </div>
        <div className="lg-progress-track" role="progressbar" aria-valuenow={levelPct} aria-valuemin={0} aria-valuemax={100}>
          <div className="lg-progress-fill" style={{ width: `${levelPct}%` }} />
        </div>
        <p className="lg-progress-meta">{xpToNext > 0 ? `${xpToNext} XP to next level` : 'Max level progress updated'}</p>
      </div>

      {daily && (
        <p className="lg-progress-daily">
          Daily goal · {daily.completed}/{daily.target} reviews
          {daily.completedToday && <span className="lg-progress-daily-done"> · Complete</span>}
        </p>
      )}

      {loading && <p className="lg-progress-status">Loading your progress…</p>}
      {apiError && (
        <p className="lg-progress-warn" role="status">
          Could not reach progress API — restart the backend with gamification enabled, then refresh.
        </p>
      )}
      {isEmpty && (
        <p className="lg-progress-status">
          Start earning XP: mark a question read, grade a practice card, or clear a review.
        </p>
      )}

      <div className="lg-progress-links">
        <Link to="/profile">My progress</Link>
        <PlanAwareLink to="/leaderboard">
          Leaderboard
          {!leaderboardLocked && rank ? ` · #${rank}` : ''}
          {leaderboardLocked && <PlanTierLabel tier="Pro" />}
        </PlanAwareLink>
      </div>
    </section>
  );
}
